"use client";

import { useCallback, useEffect, useState } from "react";
import type { Comment, CommentFormData } from "@/lib/types";
import CommentForm from "@/components/CommentForm";
import CommentItem from "@/components/CommentItem";
import { getCommentsLikeDataAction, toggleCommentLikeAction } from "@/lib/actions/commentLikes";
import { createComment, editComment, getComments, removeComment } from "@/lib/actions/comments";
import { useCommentDeepLink } from "@/hooks/useCommentDeepLink";

const COMMENTS_PER_PAGE = 3;

// 모든 댓글 ID 재귀 추출 (컴포넌트 외부 - immutability 규칙 회피)
function getAllCommentIds(list: Comment[]): string[] {
  return list.flatMap((c) => [
    c.id,
    ...(c.replies ? getAllCommentIds(c.replies) : []),
  ]);
}

// 댓글에 좋아요 수 주입 (컴포넌트 외부)
function injectLikeCounts(
  list: Comment[],
  likeCounts: Record<string, number>
): Comment[] {
  return list.map((c) => ({
    ...c,
    likeCount: likeCounts[c.id] ?? 0,
    replies: c.replies ? injectLikeCounts(c.replies, likeCounts) : [],
  }));
}

interface Props {
  postId: number;
}

/**
 * JCG-1571: 복원 게시물 전용 댓글 섹션.
 * - 기존 댓글 시스템(`getComments`/`createComment` 등)을 `restoration/{postId}` slug로 재사용.
 * - 카테고리 탭 없음 (단일 평면 목록).
 * - 글쓰기 잠금(회차)과 무관하게 항상 열림.
 */
export default function RestorationCommentSection({ postId }: Props) {
  const slug = `restoration/${postId}`;

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [likedCommentIds, setLikedCommentIds] = useState<Set<string>>(
    new Set()
  );
  const [visibleCount, setVisibleCount] = useState(COMMENTS_PER_PAGE);

  const visibleComments = comments.slice(0, visibleCount);
  const hasMore = comments.length > visibleCount;

  // 댓글 딥링크(#comment-{id}, JCG-3603) — 탭이 없으므로 visibleCount 만 대상 루트가 보이는 값까지 늘린다.
  // 이 섹션은 PostCard 가 펼쳐진 뒤에야 마운트되므로, 딥링크는 `?post={postId}` 로 게시판이 해당 카드를
  // 펼쳐 준 뒤(RestorationPageClient) 이 훅이 이어받아 스크롤·강조한다.
  const { targetCommentId: deepLinkTargetId, highlightedCommentId } = useCommentDeepLink({
    comments,
    loading,
    pageSize: COMMENTS_PER_PAGE,
    setVisibleCount,
  });

  const loadComments = useCallback(async () => {
    try {
      const result = await getComments(slug);
      if (!result.success) {
        console.error("댓글 로드 실패:", result.error);
        setComments([]);
        return;
      }
      const allIds = getAllCommentIds(result.comments);
      if (allIds.length === 0) {
        setComments(result.comments);
        return;
      }

      const likeData = await getCommentsLikeDataAction(allIds);

      if (likeData.success) {
        setComments(injectLikeCounts(result.comments, likeData.likeCounts));
        setLikedCommentIds(new Set(likeData.likedIds));
      } else {
        setComments(result.comments);
      }
    } catch (error) {
      console.error("댓글 로드 실패:", error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    // 외부 데이터 fetch(getComments + getCommentsLikeData)이므로 useEffect 유지가 적합.
    // React가 권장하는 외부 시스템 구독 패턴이며, cascading render 위험 없음.
    // JCG-2029 Phase 5-5: 향후 React Query 마이그레이션(JCG-2034) 시 제거 예정.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadComments();
  }, [loadComments]);

  // ---------- 댓글 트리 업데이트 헬퍼 ----------

  const addReplyToComment = (
    list: Comment[],
    parentId: string,
    newReply: Comment
  ): Comment[] =>
    list.map((c) => {
      if (c.id === parentId) {
        return { ...c, replies: [...(c.replies || []), newReply] };
      }
      if (c.replies && c.replies.length > 0) {
        return {
          ...c,
          replies: addReplyToComment(c.replies, parentId, newReply),
        };
      }
      return c;
    });

  const replaceReplyInComment = (
    list: Comment[],
    tempId: string,
    newReply: Comment
  ): Comment[] =>
    list.map((c) => {
      if (c.id === tempId) return { ...newReply, replies: c.replies || [] };
      if (c.replies && c.replies.length > 0) {
        return {
          ...c,
          replies: replaceReplyInComment(c.replies, tempId, newReply),
        };
      }
      return c;
    });

  const removeReplyFromComment = (
    list: Comment[],
    tempId: string
  ): Comment[] =>
    list
      .map((c) => ({
        ...c,
        replies: c.replies
          ? removeReplyFromComment(c.replies, tempId).filter(
              (r) => r.id !== tempId
            )
          : [],
      }))
      .filter((c) => c.id !== tempId);

  const updateCommentInComments = (
    list: Comment[],
    commentId: string,
    updateFn: (c: Comment) => Comment
  ): Comment[] =>
    list.map((c) => {
      if (c.id === commentId) return updateFn(c);
      if (c.replies && c.replies.length > 0) {
        return {
          ...c,
          replies: updateCommentInComments(c.replies, commentId, updateFn),
        };
      }
      return c;
    });

  // ---------- 핸들러 ----------

  const handleAddComment = async (data: CommentFormData) => {
    const tempId = `temp_${Date.now()}`;
    const tempComment: Comment = {
      id: tempId,
      content: data.content,
      author: data.author,
      email: data.email,
      createdAt: new Date().toISOString(),
      replies: [],
    };
    setComments((prev) => [tempComment, ...prev]);

    try {
      const result = await createComment(slug, data);
      if (result.success && result.comment) {
        setComments((prev) =>
          prev.map((c) =>
            c.id === tempId ? { ...result.comment!, replies: [] } : c
          )
        );
        alert("댓글이 등록되었습니다.");
        return true;
      }
      setComments((prev) => prev.filter((c) => c.id !== tempId));
      alert(result.error || "댓글 등록에 실패했습니다.");
      return false;
    } catch (error) {
      setComments((prev) => prev.filter((c) => c.id !== tempId));
      console.error("댓글 추가 실패:", error);
      alert("댓글 등록에 실패했습니다.");
      return false;
    }
  };

  const handleAddReply = async (parentId: string, data: CommentFormData) => {
    const tempId = `temp_${Date.now()}`;
    const tempReply: Comment = {
      id: tempId,
      content: data.content,
      author: data.author,
      email: data.email,
      createdAt: new Date().toISOString(),
      parentId,
      replies: [],
    };
    setComments((prev) => addReplyToComment(prev, parentId, tempReply));

    try {
      const result = await createComment(slug, data, parentId);
      if (result.success && result.comment) {
        setComments((prev) =>
          replaceReplyInComment(prev, tempId, result.comment!)
        );
        alert("답글이 등록되었습니다.");
        return true;
      }
      setComments((prev) => removeReplyFromComment(prev, tempId));
      alert(result.error || "답글 등록에 실패했습니다.");
      return false;
    } catch (error) {
      setComments((prev) => removeReplyFromComment(prev, tempId));
      console.error("답글 추가 실패:", error);
      alert("답글 등록에 실패했습니다.");
      return false;
    }
  };

  const handleEditComment = async (
    commentId: string,
    email: string,
    data: CommentFormData
  ) => {
    const original = [...comments];
    setComments((prev) =>
      updateCommentInComments(prev, commentId, (c) => ({
        ...c,
        content: data.content,
        author: data.author,
      }))
    );

    try {
      const result = await editComment(commentId, email, data, slug);
      if (result.success && result.comment) {
        setComments((prev) =>
          updateCommentInComments(prev, commentId, (c) => ({
            ...result.comment!,
            replies: c.replies || [],
            likeCount: c.likeCount,
          }))
        );
        alert("댓글이 수정되었습니다.");
        return true;
      }
      setComments(original);
      alert(result.error || "댓글 수정에 실패했습니다.");
      return false;
    } catch (error) {
      setComments(original);
      console.error("댓글 수정 실패:", error);
      alert("댓글 수정에 실패했습니다.");
      return false;
    }
  };

  const handleDeleteComment = async (commentId: string, email: string) => {
    const original = [...comments];
    setComments((prev) =>
      updateCommentInComments(prev, commentId, (c) => ({
        ...c,
        author: null,
        content: null,
        isDeleted: true,
      }))
    );
    const result = await removeComment(commentId, email, slug);
    if (result.success) {
      alert("댓글이 삭제되었습니다.");
    } else {
      setComments(original);
      alert(result.error || "댓글 삭제에 실패했습니다.");
    }
  };

  const handleLikeComment = async (commentId: string) => {
    const wasLiked = likedCommentIds.has(commentId);
    setLikedCommentIds((prev) => {
      const next = new Set(prev);
      if (wasLiked) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
    setComments((prev) =>
      updateCommentInComments(prev, commentId, (c) => ({
        ...c,
        likeCount: (c.likeCount ?? 0) + (wasLiked ? -1 : 1),
      }))
    );

    const result = await toggleCommentLikeAction(commentId);
    if (result.success) {
      setLikedCommentIds((prev) => {
        const next = new Set(prev);
        if (result.liked) next.add(commentId);
        else next.delete(commentId);
        return next;
      });
      setComments((prev) =>
        updateCommentInComments(prev, commentId, (c) => ({
          ...c,
          likeCount: result.likeCount,
        }))
      );
    } else {
      // 롤백
      setLikedCommentIds((prev) => {
        const next = new Set(prev);
        if (wasLiked) next.add(commentId);
        else next.delete(commentId);
        return next;
      });
      setComments((prev) =>
        updateCommentInComments(prev, commentId, (c) => ({
          ...c,
          likeCount: (c.likeCount ?? 0) + (wasLiked ? 1 : -1),
        }))
      );
    }
  };

  if (loading) {
    return (
      <div className="mt-4 rounded-md border-[0.5px] dark:border-neutral-500 p-4">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#706E6E] dark:border-neutral-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border-[0.5px] dark:border-neutral-500 p-3 sm:p-4">
      <h3 className="border-b-[0.5px] border-gray-200 dark:border-neutral-400 pb-2 text-base sm:text-lg font-semibold text-[#333] dark:text-neutral-200 mb-2">
        댓글 ({comments.length})
      </h3>

      <div className="space-y-0">
        {visibleComments.length > 0 ? (
          visibleComments.map((comment, index) => {
            const total = visibleComments.length;
            const shouldTruncate = index >= 2;
            return (
              <div key={comment.id}>
                <CommentItem
                  comment={comment}
                  onReply={handleAddReply}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  onLike={handleLikeComment}
                  likedCommentIds={likedCommentIds}
                  shouldTruncate={shouldTruncate}
                  deepLinkTargetId={deepLinkTargetId}
                  highlightedCommentId={highlightedCommentId}
                />
                {index < total - 1 && (
                  <hr className="border-gray-200 dark:border-neutral-600" />
                )}
              </div>
            );
          })
        ) : (
          <p className="text-center py-6 text-sm text-[#706E6E] dark:text-gray-400 font-light">
            아직 댓글이 없습니다. 첫 댓글을 남겨보세요!
          </p>
        )}
      </div>

      {hasMore && (
        <div className="flex justify-center py-3">
          <button
            onClick={() => setVisibleCount((prev) => prev + COMMENTS_PER_PAGE)}
            className="px-5 py-1.5 text-xs font-medium text-[#706E6E] dark:text-neutral-300 bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 rounded-full transition-colors"
          >
            이전 댓글 더보기 ({comments.length - visibleCount}개 남음)
          </button>
        </div>
      )}

      <div className="mt-3 p-3 border-[0.5px] border-gray-200 dark:border-neutral-400 bg-[#FAFAFA] dark:bg-gray-800 rounded-md">
        <CommentForm onSubmit={handleAddComment} />
      </div>
    </div>
  );
}
